import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRadio } from '@react-aria/radio';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useFocusRing } from '@react-aria/focus';
import IconRadioUnselected from '../../icons/icon-radio-unselected.svg';
import IconRadioSelected from '../../icons/icon-radio-selected.svg';
import IconRadioDisabled from '../../icons/icon-radio-disabled.svg';
import { RadioContext } from './RadioGroup';

const themeMap = {
  default: 'radio',
  filter: 'radio--filter',
  addOn: 'radio--addOn',
};

const Radio = function RadioComponent(props) {
  const {
    children,
    field,
    theme,
    disabled,
    blocked,
  } = props;

  const state = useContext(RadioContext);
  const ref = useRef(null);
  const labelId = `${field.id}-label`;

  const { inputProps } = useRadio({
    value: field.id,
    ['aria-labelledby']: labelId,
    ...props
  }, state, ref);
  const { isFocusVisible, focusProps } = useFocusRing();

  const radioClass = classNames(themeMap[theme], {
    [`${themeMap[theme]}--selected`]: field.selected,
    [`${themeMap[theme]}--disabled`]: disabled,
    [`${themeMap[theme]}--focused`]: isFocusVisible,
  });
  const IconComponent = field.selected ? IconRadioSelected : IconRadioUnselected;

  return (
    <label className={radioClass}>
      <VisuallyHidden elementType="span">
        <input {...inputProps} {...focusProps} ref={ref} disabled={disabled || blocked} />
      </VisuallyHidden>
      {disabled && <IconRadioDisabled className="radio__icon-disabled" />}
      {!disabled && <IconComponent aria-hidden={true} className="radio__icon" />}
      <span id={labelId} className="radio__label">{field.label}</span>
      {children}
    </label>
  );
};

Radio.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
  }).isRequired,
  fieldName: PropTypes.string.isRequired,
  theme: PropTypes.string,
  disabled: PropTypes.bool,
  blocked: PropTypes.bool,
};

Radio.defaultProps = {
  theme: 'default',
  disabled: false,
  blocked: false,
};

export default Radio;
